import { useState, useEffect, useCallback } from 'react';
import { ApiError, asList } from '../lib/api';
import { hrApi, type Employee, type LeaveType, type LeaveBalance, type LeaveRequest } from '../lib/hrApi';
import { attendanceApi, type StaffAttendance } from '../lib/payrollApi';

/// A staff member's own leave: balances, apply, history. The employee is resolved from the TOKEN —
/// you can only ever apply for yourself.
export function MyLeavePage() {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [noRecord, setNoRecord] = useState(false);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [history, setHistory] = useState<LeaveRequest[]>([]);
  const [f, setF] = useState({ leaveTypeId: '', fromDate: '', toDate: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [today, setToday] = useState<StaffAttendance | null>(null);

  const load = useCallback(() => {
    hrApi.myEmployee().then((e) => { setEmployee(e); setNoRecord(false); }).catch(() => setNoRecord(true));
    hrApi.myBalances().then((r) => setBalances(asList(r))).catch(() => setBalances([]));
    hrApi.myLeave().then((r) => setHistory(asList(r))).catch(() => setHistory([]));
    hrApi.listLeaveTypes().then((r) => setTypes(asList(r))).catch(() => setTypes([]));
    attendanceApi.mine().then((rows) => {
      const t = new Date().toISOString().slice(0, 10);
      setToday(rows.find((r) => r.date === t) ?? null);
    }).catch(() => setToday(null));
  }, []);
  useEffect(load, [load]);

  const typeName = (id: string) => types.find((t) => t.id === id)?.name ?? '—';

  const apply = async () => {
    if (!f.leaveTypeId || !f.fromDate || !f.toDate) return;
    setBusy(true); setMsg(null);
    try {
      await hrApi.applyLeave({ leaveTypeId: f.leaveTypeId, fromDate: f.fromDate, toDate: f.toDate, reason: f.reason.trim() || null });
      setF({ leaveTypeId: '', fromDate: '', toDate: '', reason: '' });
      setMsg('Leave applied — awaiting approval.');
      load();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not apply.'); }
    finally { setBusy(false); }
  };

  const badge = (s: string) => s === 'Approved' ? 'pill-ok' : s === 'Rejected' ? 'pill-danger' : 'pill-muted';

  if (noRecord) {
    return (
      <div>
        <h1 className="h-display" style={{ fontSize: 28, marginBottom: 10 }}>My leave</h1>
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-2)' }}>
          No HR record is linked to your account yet. Ask an administrator to create your employee record and link it to your login.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>My leave</h1>
        {employee && <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>{employee.fullName} · <span className="mono">{employee.employeeCode}</span></p>}
      </div>

      {/* today's attendance — self check-in */}
      <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, maxWidth: 540 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase' }}>Today</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {today ? today.status : 'Not checked in'}
            {today?.checkInAt && <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ink-3)' }}>
              {' · in '}{new Date(today.checkInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {today.checkOutAt ? ` · out ${new Date(today.checkOutAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
            </span>}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-accent" disabled={!!today?.checkInAt}
            onClick={async () => { try { setToday(await attendanceApi.checkIn()); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not check in.'); } }}>
            Check in
          </button>
          <button className="btn" disabled={!today?.checkInAt || !!today?.checkOutAt}
            onClick={async () => { try { setToday(await attendanceApi.checkOut()); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not check out.'); } }}>
            Check out
          </button>
        </div>
      </div>

      {/* balances */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {balances.length === 0 ? <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>No balances yet — they're created when your first leave of a type is approved.</p> :
          balances.map((b) => (
            <div key={b.leaveTypeId} className="card" style={{ padding: '12px 16px', minWidth: 130 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>{typeName(b.leaveTypeId)}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{b.available}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>of {b.entitled} left</div>
            </div>
          ))}
      </div>

      {/* apply */}
      <div className="card" style={{ padding: 18, marginBottom: 20, maxWidth: 540 }}>
        <h3 style={{ fontSize: 15, marginBottom: 10 }}>Apply for leave</h3>
        {msg && <div className="pill pill-muted" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{msg}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div><label className="label">Type</label>
            <select className="input" value={f.leaveTypeId} onChange={(e) => setF({ ...f, leaveTypeId: e.target.value })}>
              <option value="">Select…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label className="label">From</label><input className="input" type="date" value={f.fromDate} onChange={(e) => setF({ ...f, fromDate: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label className="label">To</label><input className="input" type="date" value={f.toDate} onChange={(e) => setF({ ...f, toDate: e.target.value })} /></div>
          </div>
          <div><label className="label">Reason (optional)</label><input className="input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
          <button className="btn btn-accent" disabled={busy || !f.leaveTypeId || !f.fromDate || !f.toDate} onClick={apply} style={{ alignSelf: 'flex-start' }}>{busy ? 'Applying…' : 'Apply'}</button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>Sundays and school holidays aren't counted. Your balance is only used once the leave is approved.</p>
      </div>

      {/* history */}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Your leave</div>
      {history.length === 0 ? <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>Nothing yet.</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 540 }}>
          {history.map((r) => (
            <div key={r.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div>
                <strong style={{ fontSize: 14 }}>{typeName(r.leaveTypeId)}</strong>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.fromDate} → {r.toDate} · {r.days} day(s){r.reviewNote ? ` · ${r.reviewNote}` : ''}</div>
              </div>
              <span className={`pill ${badge(r.status)}`} style={{ marginLeft: 'auto', fontSize: 11 }}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
